/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext } from "react";
import DatePicker from "react-datepicker";
import { useParams } from "react-router-dom";

import { PatientsContext } from ".";
import fetchHelper from "../../services/fetchHelper";
import savingSpinner from "../../assets/esign-ui-icons/saving-ico-white.svg";
import { LayoutContext } from "../../components/layout";
import { MANUFACTURES, LEAD_STATUS, LEAD_TYPES } from "../../constants/PatientConstants";
import { toISODate } from "../../utils/helpers";

const LEADS_API_BASE_PATH = "/patientmanagement/rest/leads";

const Form = ({ data, onSaveOrEdit, onDelete }) => {
  const { i } = useParams();
  const advisoryList = [
    { displayText: "NO", value: false },
    { displayText: "YES", value: true },
  ];
  const emptyLead = {
    leadType: "LV",
    manufacturerName: "BOSTONSCI",
    status: "IMPLANTED",
    patient: { id: i },
    advisory: false,
  };

  const { leads, setLeads } = useContext(PatientsContext);
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const { headerDetails, updateHeaderDetails } = useContext(LayoutContext);

  const [lead, setLead] = useState(data || emptyLead);
  const [pristineLead, setPristineLead] = useState(data || emptyLead);

  const save = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setSaving(true);
    setLeads({ saving: true });

    try {
      let d;
      if (onSaveOrEdit) {
        d = await onSaveOrEdit(lead);
      }
      if (lead.id) {
        setLeads({ type: "update", lead: d });
      } else {
        setLeads({ type: "add", lead: d });
      }
      setIsEdit(false);

      // If we update the lead that is being displayed in the header then we need
      // to update that data in the layout context
      if (headerDetails && headerDetails.patient && headerDetails.patient.leads.length) {
        const leadIndex = headerDetails.patient.leads.findIndex((lead) => lead.id === d.id);
        if (leadIndex > -1) {
          const leads = [...headerDetails.patient.leads];
          leads.splice(leadIndex, 1);
          leads.push(d);
          const result = {
            ...headerDetails,
            patient: { ...headerDetails.patient, leads },
          };
          updateHeaderDetails(result);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    setRemoving(true);
    try {
      onDelete && (await onDelete(lead.id));
      setLeads({ type: "remove", lead });
    } catch (err) {
      console.error(err);
    } finally {
      setRemoving(false);
    }
  };

  const onCancelEdit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setIsEdit(false);
    setLead(pristineLead);
  };

  useEffect(() => {
    if (leads.saved && !lead.id) {
      save();
    }
  }, [leads.saved]);

  const onChange = (e) => {
    const element = e.target;
    const key = element.dataset.key;
    let value = element.value;
    setLead({ ...lead, [key]: value });
  };

  return (
    <>
      <div className="row rowStretch location-row" style={{ position: "relative" }}>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Lead Type</span>
            <select value={lead.leadType || "LV"} data-key="leadType" onChange={onChange}>
              <option value="">Select</option>
              {LEAD_TYPES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Manufacturer</span>
            <select
              value={lead.manufacturerName || "BOSTONSCI"}
              data-key="manufacturerName"
              onChange={onChange}
            >
              <option value="">Select</option>
              {MANUFACTURES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Lead Status</span>
            <select value={lead.status || "IMPLANTED"} data-key="status" onChange={onChange}>
              <option value="">Select</option>
              {LEAD_STATUS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Serial Number</span>
            <input type="text" value={lead.serialNumber} data-key="serialNumber" onChange={onChange} />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label>
            <span>Lead Model</span>
            <input type="text" value={lead.leadModel} data-key="leadModel" onChange={onChange} />
          </label>
        </div>
        <div className="col col-s-100 col-m-50 col33">
          <label className="inCal">
            <span>Implant Date</span>
          </label>
          <DatePicker
            selected={(lead?.implantDate && new Date(lead?.implantDate.replaceAll("-", "/"))) || ""}
            onChange={(date) => {
              setLead({ ...lead, ["implantDate"]: toISODate(date) });
            }}
          />
        </div>
        {!isEdit && lead.id && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255,255,255,0.25)",
            }}
          />
        )}
      </div>
      <div
        className="row rowStretch location-row"
        style={{ borderBottom: "1px solid #ccc", marginBottom: 22.5 }}
      >
        {lead.id && (
          <div className="col col-s-100 col-m-50 col33" style={{ display: "flex", marginTop: "24px" }}>
            {isEdit ? (
              <button className="btn btnTeal btn-sm" style={{ width: "87px" }} onClick={save}>
                {saving ? <img id="loading-spinner-1" src={savingSpinner} style={{ width: 15 }} /> : "Save"}
              </button>
            ) : (
              <button className="btn btn-sm" style={{ width: "87px" }} onClick={(e) => setIsEdit(true)}>
                Edit
              </button>
            )}
            {isEdit ? (
              <button className="btn btn-sm" style={{ width: "87px" }} onClick={onCancelEdit}>
                Cancel
              </button>
            ) : (
              <button className="btn btn-sm btn-urgent" style={{ width: "87px" }} onClick={remove}>
                {removing ? (
                  <img id="loading-spinner-2" src={savingSpinner} style={{ width: 15 }} />
                ) : (
                  "Remove"
                )}
              </button>
            )}
          </div>
        )}
        <div className="col col-s-100 col-m-50 col33"></div>
        <div className="col col-s-100 col-m-50 col33" style={!lead.id ? { width: "10px" } : null}>
          <label>
            <span>Advisory</span>
            <select value={lead.advisory} data-key="advisory" onChange={onChange}>
              {advisoryList.map((item, index) => (
                <option key={index} value={item.value}>
                  {item.displayText}
                </option>
              ))}
            </select>
          </label>
          {!isEdit && lead.id && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255,255,255,0.15)",
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

const Lead = () => {
  const { leads: cleads } = useContext(PatientsContext);
  const [leads, setLeads] = useState([]);
  const { i } = useParams();

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const leads = await fetchHelper.get({ url: `${LEADS_API_BASE_PATH}/findByPatientId/${i}` });
      setLeads(leads);
    } catch (error) {
      console.error(error);
    }
  };

  const onSaveOrEdit = async (lead) => {
    let updatedLead;
    if (lead.id) {
      updatedLead = await fetchHelper.put(`${LEADS_API_BASE_PATH}/${lead.id}`, lead);
    } else {
      updatedLead = await fetchHelper.post(LEADS_API_BASE_PATH, lead);
    }
    await fetchLeads();
    return updatedLead;
  };

  const onDelete = async (leadId) => {
    await fetchHelper.delete(`${LEADS_API_BASE_PATH}/${leadId}`);
    await fetchLeads();
  };

  return (
    <div className="tabContent lead-information">
      <div className="content">
        <div className="grpHdr">
          <h3>Lead Information</h3>
        </div>
        {cleads.openForm && <Form onSaveOrEdit={onSaveOrEdit} onDelete={onDelete} />}
        {leads.map((lead) => (
          <Form data={lead} key={lead.id} onSaveOrEdit={onSaveOrEdit} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
};

export default Lead;
